import { Alert } from '@apollo/orbit';
import React from 'react';

import {
  CloudProvider,
  RouterStatus,
  ShardStatus,
} from 'src/lib/graphqlTypes/types';

import { GraphRef } from '../../hooks/useGraphRef';
import { launchesRouteConfig } from '../../routes';
import { AlertWithCTA } from '../components/AlertWithCTA';

import { durationInDays, useCreateRouter } from './helpers';
import { RouterStatusMask } from './types';

const defaultCTA = {
  href: 'https://www.apollographql.com/docs/graphos/cloud-routing/serverless',
  text: 'Learn more',
};

const migrationCTA = {
  href: 'https://www.apollographql.com/docs/graphos/cloud-routing/serverless/aws-migration/',
  text: 'Learn more',
};

const erroredCTA = ({
  graphRef,
  launchId,
}: {
  graphRef: GraphRef;
  launchId: string;
}) => ({
  to: launchesRouteConfig.location({
    ...graphRef,
    launchId,
  }),
  text: 'Learn more',
});

const CreatingAlert = () => (
  <AlertWithCTA
    status="info"
    id="alert-router-recreating"
    message="Router is being recreated. This can take up to 5 minutes."
    maybeCTA={defaultCTA}
  />
);

const DeletedAlert = (graphRef: GraphRef) => {
  const { createRouter } = useCreateRouter(graphRef);
  const actionProps = {
    action: createRouter,
    text: 'Recreate your Router',
  };

  return (
    <AlertWithCTA
      status="warning"
      id="alert-router-delete"
      message="Your cloud router has been deleted but your studio graph variant remains."
      maybeAction={actionProps}
    />
  );
};

const DeprecatedAlert = () => {
  return (
    <AlertWithCTA
      status="error"
      id="alert-shard-deprecated-warning"
      message="Your cloud router is marked for deletion. Please migrate to continue serving traffic."
      maybeCTA={migrationCTA}
    />
  );
};

const DeleteWarningAlert = (router: RouterStatusMask) => {
  if (!router) {
    return NullAlert();
  }

  const message = `Your cloud router hasn't received traffic in ${durationInDays(
    router.constants?.durationBeforeDeleteSecs,
  )} days and will be deleted soon.`;

  return (
    <AlertWithCTA
      status="error"
      id="alert-router-delete-warning"
      message={message}
      maybeCTA={defaultCTA}
    />
  );
};

const SleepingAlert = (router: RouterStatusMask) => {
  if (!router) {
    return NullAlert();
  }
  const message = `Your cloud router hasn't received traffic in more than ${durationInDays(
    router.constants?.durationBeforeSleepSecs,
  )} days and is now sleeping. Start receiving traffic to wake your router.`;

  return (
    <AlertWithCTA
      status="warning"
      id="alert-router-sleep-warning"
      message={message}
      maybeCTA={defaultCTA}
    />
  );
};

const SleepWarningAlert = (router: RouterStatusMask) => {
  if (!router) {
    return NullAlert();
  }
  const message = `Your cloud router hasn't recieved traffic in ${durationInDays(
    router.constants?.durationBeforeSleepSecs,
  )} days and is scheduled to go to sleep soon.`;

  return (
    <AlertWithCTA
      status="warning"
      id="alert-router-sleep-warning"
      message={message}
      maybeCTA={defaultCTA}
    />
  );
};

const SuccessAlert = () => (
  <Alert status="success" key="alert-recreated-router">
    Your router has been successfully recreated.
  </Alert>
);

const ErrorAlert = ({
  graphRef,
  launchId,
}: {
  graphRef: GraphRef;
  launchId: string;
}) => {
  const errorRouteFormat = erroredCTA({ graphRef, launchId });

  return (
    <AlertWithCTA
      status="warning"
      id="alert-router-sleep-warning"
      message="Your router has not successfully been recreated."
      maybeRoute={errorRouteFormat}
    />
  );
};

const NullAlert = () => <></>;

export const CloudRouterLifeCycleAlert = ({
  router,
  graphRef,
  launchId,
  routerIsRecreated,
  routerRecreateIsErrored,
}: {
  router: RouterStatusMask | undefined | null;
  graphRef: GraphRef | undefined | null;
  launchId: string | undefined | null;
  routerIsRecreated: boolean;
  routerRecreateIsErrored: boolean;
}) => {
  if (!router || !graphRef) {
    return NullAlert();
  }

  if (routerIsRecreated) {
    return SuccessAlert();
  }

  if (routerRecreateIsErrored) {
    return ErrorAlert({ graphRef, launchId: launchId ?? '' });
  }

  if (router.shard?.region?.provider === CloudProvider.FLY) {
    return DeprecatedAlert();
  }

  switch (router.shard?.status) {
    case ShardStatus.DEPRECATED:
      return DeprecatedAlert();
    default:
      break; // Do nothing
  }

  switch (router.nextStatus) {
    case RouterStatus.SLEEPING:
      return SleepWarningAlert(router);
    case RouterStatus.DELETED:
      return DeleteWarningAlert(router);
    case RouterStatus.RUNNING:
      return CreatingAlert();
    default:
      break; // do nothing and fall through. Our lint settings require a default branch.
  }

  switch (router.status) {
    case RouterStatus.DELETED:
      return DeletedAlert(graphRef);
    case RouterStatus.CREATING:
      return CreatingAlert();
    case RouterStatus.SLEEPING:
      return SleepingAlert(router);
    default: {
      return NullAlert();
    }
  }
};
